import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box, Typography, Button } from "@mui/material";
import { useApi } from "../api/useApi";
import { useAuth } from "../auth/AuthContext";
import GlassLoaderOverlay from "../components/GlassLoader";

/**
 * Landing point for the "Resume my assessment" link sent in the
 * incomplete-assessment reminder email. Skips OTP: validates the token
 * against GET /api/user/resume, then logs the user in and drops them
 * straight into /questions/{domain} where they left off.
 */
export default function ResumePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const { request } = useApi();

  const [status, setStatus] = useState("checking"); // checking | error
  const [message, setMessage] = useState("");

  useEffect(() => {
    const token = searchParams.get("token");

    if (!token) {
      setStatus("error");
      setMessage("This resume link is missing its token. Please log in instead.");
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const data = await request({
          url: "/user/resume",
          method: "GET",
          params: { token },
        });

        if (cancelled) return;

        login({
          name: data.name,
          email: data.email,
          domain: data.domain,
        });

        navigate(`/questions/${data.domain}`, { replace: true });
      } catch (err) {
        if (cancelled) return;

        const reason = err?.reason;
        if (reason === "EXPIRED") {
          setMessage("This resume link has expired. Please log in to continue your assessment.");
        } else if (reason === "NO_LONGER_RESUMABLE") {
          setMessage("This assessment has already been completed or has expired. Please log in to see your latest status.");
        } else {
          setMessage("This resume link isn't valid. Please log in instead.");
        }
        setStatus("error");
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (status === "checking") {
    return <GlassLoaderOverlay open />;
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
        gap: 2,
        textAlign: "center",
        px: 3,
      }}
    >
      <Typography variant="h6">{message}</Typography>
      <Button variant="contained" onClick={() => navigate("/login")}>
        Go to login
      </Button>
    </Box>
  );
}